import Controller from '@ember/controller';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action, get, set } from '@ember/object';
import config from 'garaje/config/environment';

const TIME_RANGE_MS = {
  'Last 1 hour': 60 * 60 * 1000,
  'Last 24 hours': 24 * 60 * 60 * 1000,
  'Last 7 days': 7 * 24 * 60 * 60 * 1000,
  'Last 30 days': 30 * 24 * 60 * 60 * 1000,
};

function formatTimestampDate(epochMs) {
  return new Date(epochMs).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function formatTimestampTime(epochMs) {
  return new Date(epochMs).toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

function normalizeOutcome(outcome) {
  if (!outcome) return outcome;
  return outcome.replace(/^OPERATION_STATUS_/, '');
}

function formatOperationType(opType) {
  if (!opType) return null;
  return opType
    .replace(/^INTEGRATION_OPERATION_TYPE_/, '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDirection(direction) {
  if (!direction) return null;
  return direction
    .replace(/^OPERATION_DIRECTION_/, '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function mapTimelineEvent(raw) {
  return {
    eventId: raw.eventId,
    installId: raw.installId,
    processedAtMs: raw.envoyProcessedAt, // kept for chart bucketing
    processedDateDisplay: formatTimestampDate(raw.envoyProcessedAt),
    processedTimeDisplay: formatTimestampTime(raw.envoyProcessedAt),
    integrationType: raw.integrationType,
    integrationName: raw.integrationType, // enriched in _mappedEvents via installId lookup
    locationName: raw.locationName ?? (raw.locationId != null ? String(raw.locationId) : null),
    operationType: raw.operation?.type,
    operationTypeLabel: formatOperationType(raw.operation?.type),
    operationDefinition: raw.operation?.definition,
    operationStatus: normalizeOutcome(raw.outcome),
    failureType: raw.status ?? null,
    failureReason: raw.failureReason ?? null,
    displayReason: raw.displayReason ?? null,
    errorDetail: raw.errorDetail ?? null,
    recommendedAction: raw.recommendedAction ?? null,
    helpCenterUrl: raw.helpCenterUrl ?? null,
    direction: formatDirection(raw.direction),
    responsibleParty: raw.responsibleParty ?? null,
    primaryMetric: raw.primaryMetric ?? null,
    endpoint: raw.endpoint ?? null,
    durationMillis: raw.durationMillis,
    correlationId: raw.correlationId ?? null,
  };
}

export default class IntegrationHealthDashboardController extends Controller {
  @tracked selectedTimeRange = 'Last 1 hour';
  @tracked selectedInstall = null; // null = All installs
  @tracked selectedEventType = 'All event types';
  @tracked selectedLocation = 'All locations';
  @tracked selectedIntegrationType = 'All integration types';
  @tracked selectedStatus = 'All statuses';
  @tracked isLoading = false;
  @tracked isFilterDrawerOpen = false;
  @tracked page = 1;
  @tracked expandedEventId = null;
  @tracked _hoveredDotIndex = null;
  @tracked selectedEvent = null;
  @tracked relatedEvents = null; // null = not fetched / loading, [] = fetched but empty
  @tracked _relatedEventsLoading = false;
  @tracked isRelatedDrawerOpen = false;

  _relatedFetchGeneration = 0;

  @service ajaxFetch;
  @service state;
  @service currentAdmin;

  perPage = 20;
  _refreshTimer = null;

  @tracked _healthDataGeneral = null; // Contract 2a: GET /api/v1/monitoring/health — no time filter (used for dropdown filters)
  @tracked _healthDataCurrentRange = null; // Contract 2b: GET /api/v1/monitoring/health?startTime=…&endTime=… (selected window)
  @tracked _healthDataPreviousRange = null; // Contract 2c: same endpoint, window immediately before currentRange (comparison baseline for cards)
  @tracked _timelineData = null; // Contract 3: GET /api/v1/debugging/timeline

  timeRangeOptions = ['Last 1 hour', 'Last 24 hours', 'Last 7 days', 'Last 30 days'];

  get _mappedEvents() {
    const nameById = Object.fromEntries(this._integrations.map((i) => [String(i.installId), i.integrationName]));
    return (this._timelineData?.events ?? []).map((raw) => {
      const event = mapTimelineEvent(raw);
      event.integrationName = nameById[String(raw.installId)] ?? event.integrationType;
      return event;
    });
  }

  get _meta() {
    return this._timelineData?.meta ?? { total: 0, page: 1, perPage: this.perPage };
  }

  get _integrations() {
    return this._healthDataGeneral?.integrations ?? [];
  }

  get _filteredIntegrations() {
    const isGlobal = this.currentAdmin.isGlobalAdmin;
    const myLocationId = this.state.currentLocation?.id;
    return this._integrations.filter((i) => {
      if (this.selectedInstall != null) return String(i.installId) === String(this.selectedInstall);
      if (!isGlobal) return i.location == null || String(i.location.id) === String(myLocationId);
      if (this.selectedLocation !== 'All locations') return String(i.location?.id) === String(this.selectedLocation);
      return true;
    });
  }

  get installOptions() {
    const isGlobal = this.currentAdmin.isGlobalAdmin;
    const myLocationId = this.state.currentLocation?.id;

    const installs = this._integrations
      .filter((i) => {
        // Global admins see everything.
        // Location admins see their location's integrations + company-scoped ones (no location).
        if (!isGlobal) {
          return i.location == null || String(i.location.id) === String(myLocationId);
        }
        return true;
      })
      .map((i) => {
        const locLabel = i.location ? (i.location.name ?? String(i.location.id)) : null;
        const value = String(i.installId);
        return {
          label: locLabel ? `${i.integrationName} — ${locLabel}` : i.integrationName,
          value,
          integrationName: i.integrationName,
          locationId: i.location?.id ?? null,
          locationName: locLabel,
        };
      });
    return [{ label: 'All integrations', value: '' }, ...installs];
  }

  get locationOptions() {
    const seen = new Set();
    const locs = [];
    for (const i of this._integrations) {
      // Company-scoped integrations have no location — skip them here
      if (!i.location) continue;
      if (!seen.has(i.location.id)) {
        seen.add(i.location.id);
        locs.push({ label: i.location.name ?? String(i.location.id), value: String(i.location.id) });
      }
    }
    // If the selected location isn't in the health data (e.g. no integrations there yet,
    // or health hasn't loaded), still include it so the dropdown stays functional.
    if (this.selectedLocation !== 'All locations' && !seen.has(this.selectedLocation)) {
      locs.unshift({ label: this.selectedLocation, value: this.selectedLocation });
    }
    return [{ label: 'All locations', value: 'All locations' }, ...locs];
  }

  get eventTypeOptions() {
    const seen = new Set();
    const types = [];
    for (const i of this._integrations) {
      for (const opType of i.activeOperationTypes ?? []) {
        if (!seen.has(opType)) {
          seen.add(opType);
          const stripped = opType.replace(/^INTEGRATION_OPERATION_TYPE_/, '');
          const label = stripped
            .toLowerCase()
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
          types.push({ label, value: opType });
        }
      }
    }
    return [{ label: 'All event types', value: 'All event types' }, ...types];
  }

  get selectedLocationOption() {
    return this.locationOptions.find((opt) => opt.value === this.selectedLocation) ?? null;
  }

  get selectedInstallOption() {
    const val = this.selectedInstall ?? '';
    return this.installOptions.find((opt) => opt.value === val) ?? null;
  }

  get selectedEventTypeOption() {
    return this.eventTypeOptions.find((opt) => opt.value === this.selectedEventType) ?? null;
  }

  get integrationTypeOptions() {
    const seen = new Set();
    const types = [];
    for (const i of this._integrations) {
      if (i.integrationType && !seen.has(i.integrationType)) {
        seen.add(i.integrationType);
        const label = i.integrationType
          .replace(/^INTEGRATION_TYPE_/, '')
          .toLowerCase()
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase());
        types.push({ label, value: i.integrationType });
      }
    }
    return [{ label: 'All integration types', value: 'All integration types' }, ...types];
  }

  statusOptions = [
    { label: 'All statuses', value: 'All statuses' },
    { label: 'Success', value: 'OPERATION_STATUS_OK' },
    { label: 'Failed', value: 'OPERATION_STATUS_FAILED' },
    { label: 'Partial', value: 'OPERATION_STATUS_PARTIAL' },
  ];

  // Shared chart computation — accessed by individual chart getters below.
  // Returns a plain object only used internally; never passed directly to the template.
  get _chartComputed() {
    const points = this._timelineData?.chartData ?? [];
    if (!points.length) return null;

    const nonEmptyPoints = points.filter((p) => !p.isEmpty);
    if (!nonEmptyPoints.length) return null;

    const rates = nonEmptyPoints.map((p) => p.successRate);
    const minRate = Math.min(...rates);
    const maxRate = Math.max(...rates);
    const is1hRange = get(this, 'selectedTimeRange') === 'Last 1 hour';
    // For 1h zoom in tightly so small variations are visible; other ranges use coarser steps
    const buffer = is1hRange ? 2 : 5;
    const step = is1hRange ? 5 : 10;
    const yMin = Math.max(0, Math.floor((minRate - buffer) / step) * step);
    const yMax = Math.min(100, Math.ceil((maxRate + buffer) / step) * step);
    const yRange = yMax - yMin || 1;

    const yScale = (rate) => Math.round(90 - ((rate - yMin) / yRange) * 80);

    const singlePoint = points.length === 1;
    const slotW = 624 / points.length;
    const gap = Math.max(1, Math.round(slotW * 0.12));
    const bw = Math.max(1, Math.floor(slotW - gap));

    const pts = points.map((p, i) => ({
      x: Math.round(i * slotW + slotW / 2), // bar center — used for hover proximity + labels
      barX: Math.round(i * slotW),
      barWidth: bw,
      y: p.isEmpty ? null : yScale(p.successRate),
      barHeight: p.isEmpty ? 0 : 90 - yScale(p.successRate),
      startMs: p.startMs,
      successRate: p.isEmpty ? null : p.successRate,
      isEmpty: p.isEmpty ?? false,
    }));

    // Always render 7 evenly-distributed labels: first bucket, 5 interior, last bucket ("Now").
    // Bucket counts from the backend: 1h=12x5m | 24h=24x1h | 7d=21x8h | 30d=30x1d
    const is1h = get(this, 'selectedTimeRange') === 'Last 1 hour';
    const is24h = get(this, 'selectedTimeRange') === 'Last 24 hours';
    let xLabels;
    if (singlePoint) {
      xLabels = [{ x: 312, label: 'Now', textAnchor: 'middle' }];
    } else {
      const targetLabelCount = 7;
      const labelIndices = Array.from({ length: targetLabelCount }, (_, i) =>
        Math.round((i * (pts.length - 1)) / (targetLabelCount - 1)),
      );
      const lastIdx = pts.length - 1;
      xLabels = labelIndices.map((i, li) => ({
        x: pts[i].x,
        label:
          i === lastIdx
            ? 'Now'
            : is1h || is24h
              ? new Date(pts[i].startMs).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
              : new Date(pts[i].startMs).toLocaleString('en-US', { month: 'short', day: 'numeric' }),
        textAnchor: li === 0 ? 'start' : li === labelIndices.length - 1 ? 'end' : 'middle',
      }));
    }

    const mid = Math.round((yMax + yMin) / 2);
    const gridLines = [yMax, mid, yMin].map((rate) => ({
      y: yScale(rate),
      labelY: yScale(rate) + 4,
      label: `${rate}%`,
    }));

    return { bars: pts, dots: pts, xLabels, gridLines };
  }

  // Individual chart getters expose only primitives/arrays to the template,
  // avoiding Ember's "dynamic component definition" error when returning POJOs.
  get hasChartData() {
    return this._chartComputed !== null;
  }
  get chartBars() {
    return this._chartComputed?.bars ?? [];
  }
  get chartHoveredBarIndex() {
    return this._hoveredDotIndex;
  }
  get chartGridLines() {
    return this._chartComputed?.gridLines ?? [];
  }
  get chartDots() {
    return this._chartComputed?.dots ?? [];
  }
  get chartXLabels() {
    return this._chartComputed?.xLabels ?? [];
  }

  get chartHoveredPoint() {
    if (this._hoveredDotIndex === null) return null;
    const dots = this._chartComputed?.dots ?? [];
    const dot = dots[this._hoveredDotIndex];
    if (!dot || dot.isEmpty) return null;

    const d = new Date(dot.startMs);
    const datePart = d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const timePart = d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    const dateValue = `${datePart}, ${timePart}`;

    const tooltipW = 112;
    const tooltipH = 34;
    const tooltipX = Math.min(Math.max(dot.x - tooltipW / 2, 0), 624 - tooltipW);
    const tooltipY = dot.y - tooltipH - 6 < 2 ? dot.y + 6 : dot.y - tooltipH - 6;

    return {
      x: dot.x,
      y: dot.y,
      dateValue,
      successRate: dot.successRate != null ? parseFloat(dot.successRate.toFixed(2)) : dot.successRate,
      tooltipX,
      tooltipY,
      tooltipW,
      tooltipH,
      labelX: tooltipX + 7,
      valueX: tooltipX + tooltipW - 7,
      row1Y: tooltipY + 13,
      row2Y: tooltipY + 27,
    };
  }

  @action onChartMouseMove(event) {
    const svg = event.currentTarget;
    const rect = svg.getBoundingClientRect();
    const mouseX = ((event.clientX - rect.left) / rect.width) * 624;
    const dots = this._chartComputed?.dots ?? [];
    if (!dots.length) return;
    let closest = -1;
    let minDist = Infinity;
    for (let i = 0; i < dots.length; i++) {
      if (dots[i].isEmpty) continue;
      const dist = Math.abs(dots[i].x - mouseX);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    }
    this._hoveredDotIndex = closest === -1 ? null : closest;
  }

  @action onChartLeave() {
    this._hoveredDotIndex = null;
  }

  get filteredEvents() {
    return this._mappedEvents;
  }

  get recordCount() {
    return this._meta.total;
  }

  get pageCount() {
    return Math.ceil(this.recordCount / this.perPage);
  }

  get paginatedEvents() {
    return this._mappedEvents;
  }

  startAutoRefresh() {
    this.stopAutoRefresh();
    this._refreshTimer = setInterval(() => this.fetchData(), 60_000);
  }

  stopAutoRefresh() {
    if (this._refreshTimer) {
      clearInterval(this._refreshTimer);
      this._refreshTimer = null;
    }
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.stopAutoRefresh();
  }

  async fetchData() {
    this.isLoading = true;
    const now = Date.now();
    const rangeMs = TIME_RANGE_MS[get(this, 'selectedTimeRange')];
    const startTime = now - rangeMs;
    const previousRangeEnd = startTime;
    const previousRangeStart = startTime - rangeMs;

    const companyId = String(this.state.currentCompany?.id);
    const params = {
      companyId,
      startTime: String(startTime),
      endTime: String(now),
      page: String(this.page),
      perPage: String(this.perPage),
    };
    // eslint-disable-next-line ember/use-ember-get-and-set
    if (this.selectedInstall != null) params.installId = String(this.selectedInstall);
    // eslint-disable-next-line ember/use-ember-get-and-set
    if (this.selectedEventType !== 'All event types') params.eventType = this.selectedEventType;
    // eslint-disable-next-line ember/use-ember-get-and-set
    if (this.selectedLocation !== 'All locations') params.location = String(this.selectedLocation);
    // eslint-disable-next-line ember/use-ember-get-and-set
    if (this.selectedStatus !== 'All statuses') params.outcome = this.selectedStatus;
    // eslint-disable-next-line ember/use-ember-get-and-set
    if (this.selectedIntegrationType !== 'All integration types') params.integrationType = this.selectedIntegrationType;

    const rangeFilters = {
      ...(this.selectedInstall != null && { installId: String(this.selectedInstall) }),
      ...(this.selectedEventType !== 'All event types' && { eventType: this.selectedEventType }),
      ...(this.selectedLocation !== 'All locations' && { location: String(this.selectedLocation) }),
      ...(this.selectedStatus !== 'All statuses' && { outcome: this.selectedStatus }),
      ...(this.selectedIntegrationType !== 'All integration types' && {
        integrationType: this.selectedIntegrationType,
      }),
    };

    try {
      const isGlobal = this.currentAdmin.isGlobalAdmin;
      const locationId = this.state.currentLocation?.id;
      const [healthDataGeneral, healthDataCurrentRange, healthDataPreviousRange, timelineData] = await Promise.all([
        this.ajaxFetch.request(
          `${config.integrationTelemetryApiHost}/a/integration-telemetry/api/v1/monitoring/health`,
          {
            data: {
              companyId,
              operationScope: 'OPERATION_SCOPE_VENDOR_API_CALL',
              ...(!isGlobal && locationId && { locationId: String(locationId) }),
            },
          },
        ),
        this.ajaxFetch.request(
          `${config.integrationTelemetryApiHost}/a/integration-telemetry/api/v1/monitoring/health`,
          {
            data: {
              companyId,
              operationScope: 'OPERATION_SCOPE_VENDOR_API_CALL',
              startTime: String(startTime),
              endTime: String(now),
              ...rangeFilters,
            },
          },
        ),
        this.ajaxFetch.request(
          `${config.integrationTelemetryApiHost}/a/integration-telemetry/api/v1/monitoring/health`,
          {
            data: {
              companyId,
              operationScope: 'OPERATION_SCOPE_VENDOR_API_CALL',
              startTime: String(previousRangeStart),
              endTime: String(previousRangeEnd),
              ...rangeFilters,
            },
          },
        ),
        this.ajaxFetch.request(
          `${config.integrationTelemetryApiHost}/a/integration-telemetry/api/v1/debugging/timeline`,
          { data: params },
        ),
      ]);
      this._healthDataGeneral = healthDataGeneral;
      this._healthDataCurrentRange = healthDataCurrentRange;
      this._healthDataPreviousRange = healthDataPreviousRange;
      this._timelineData = timelineData;
    } finally {
      this.isLoading = false;
    }
  }

  @action setTimeRange(value) {
    set(this, 'selectedTimeRange', value);
    this.page = 1;
    this.fetchData();
  }

  @action setEventType(opt) {
    this.selectedEventType = opt?.value ?? opt;
    this.page = 1;
    this.fetchData();
  }
  @action setLocation(opt) {
    const value = opt?.value ?? opt;
    this.selectedLocation = value === 'All locations' ? 'All locations' : String(value);
    this.selectedInstall = null;
    this.page = 1;
    this.fetchData();
  }
  @action setPage(value) {
    this.page = value;
    if (value === 1) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
    this.fetchData();
  }
  @action toggleExpand(eventId) {
    this.expandedEventId = this.expandedEventId === eventId ? null : eventId;
  }

  @action openDrawer(event) {
    if (event.operationStatus === 'OK') return;
    this.selectedEvent = event;
    this.relatedEvents = null;
    this.isRelatedDrawerOpen = false;
    if (event.correlationId) {
      this._relatedEventsLoading = true;
      this.fetchRelatedEvents(event.correlationId);
    }
  }

  @action closeDrawer() {
    this.selectedEvent = null;
    this.relatedEvents = null;
    this.isRelatedDrawerOpen = false;
  }

  async fetchRelatedEvents(correlationId) {
    const gen = ++this._relatedFetchGeneration;
    this._relatedEventsLoading = true;
    const companyId = String(this.state.currentCompany?.id);
    const endTime = Date.now();
    const startTime = endTime - 30 * 24 * 60 * 60 * 1000; // last 30 days
    try {
      const data = await this.ajaxFetch.request(
        `${config.integrationTelemetryApiHost}/a/integration-telemetry/api/v1/debugging/timeline`,
        { data: { companyId, correlationId, startTime: String(startTime), endTime: String(endTime), perPage: '50' } },
      );
      if (gen !== this._relatedFetchGeneration) return;
      const nameById = Object.fromEntries(this._integrations.map((i) => [String(i.installId), i.integrationName]));
      const currentEventId = this.selectedEvent?.eventId;
      this.relatedEvents = (data?.events ?? [])
        .map((raw) => {
          const event = mapTimelineEvent(raw);
          event.integrationName = nameById[String(raw.installId)] ?? event.integrationType;
          return event;
        })
        .filter((e) => e.eventId !== currentEventId);
    } catch {
      if (gen !== this._relatedFetchGeneration) return;
      this.relatedEvents = [];
    } finally {
      if (gen === this._relatedFetchGeneration) {
        this._relatedEventsLoading = false;
      }
    }
  }

  @action openRelatedDrawer() {
    this.isRelatedDrawerOpen = true;
  }

  @action closeRelatedDrawer() {
    this.isRelatedDrawerOpen = false;
  }

  @action openRelatedEventDetail(event) {
    if (event.operationStatus === 'OK') return;
    this.selectedEvent = event;
    this.isRelatedDrawerOpen = false;
    this.relatedEvents = null;
    if (event.correlationId) {
      this._relatedEventsLoading = true;
      this.fetchRelatedEvents(event.correlationId);
    }
  }

  @action openFilterDrawer() {
    this.isFilterDrawerOpen = true;
  }

  @action closeFilterDrawer() {
    this.isFilterDrawerOpen = false;
  }

  @action setIntegrationType(value) {
    this.selectedIntegrationType = value;
    this.page = 1;
    this.fetchData();
  }

  @action setStatus(value) {
    this.selectedStatus = value;
    this.page = 1;
    this.fetchData();
  }

  @action setInstall(opt) {
    const value = opt?.value ?? opt;
    this.selectedInstall = value === '' ? null : value;
    // Auto-select the install's location. Use String for consistent eq comparisons in the template.
    // Global integrations (matched by integrationType) have no location — reset to "All locations".
    const installData = this._integrations.find((i) => String(i.installId) === this.selectedInstall);
    this.selectedLocation = installData?.location ? String(installData.location.id) : 'All locations';
    this.selectedIntegrationType = installData?.integrationType ?? 'All integration types';
    this.page = 1;
    this.fetchData();
  }
}
